import React, { ReactElement } from 'react';

import { ProgressBar, TimelineEvent, TokenList, TokenListItem } from '@philips-internal/oswald-ui-components';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Type } from '@philips-internal/oswald-ui-constants';
import { Value } from '@philips-internal/oswald-ui-core';

import { getChemotherapyStatusIcon, getStatusIcon } from '../legends';
import { ChemotherapyMedicationType, ChemotherapyStatus, MedicationStatus } from '../../administration-schedule/administration-schedule-types';
import { PrescriptionType } from '../../../services/timeline-service';
import { TimelineCpoeMaterialResponse, TimeLineMaterials } from '../electronic-service-timeline-response';

export function SimpleCard(item: Readonly<TimelineEvent>): ReactElement {
  const {
    pdoPrn,
    itemType,
    chemotherapy,
    isMedication,
    delayed,
    description,
    reasonForsettlementCode,
    status,
    chemotherapyStatus,
    cpoeMaterial,
    prescriptionDescription,
    emarStepNumber,
    totalSolutionStep,
  } = item
  const material = cpoeMaterial as TimelineCpoeMaterialResponse | undefined;
  const getDescriptionByItemType = (): string => {
    switch (itemType) {
      case PrescriptionType.ADULT_PARENTERAL_NUTRITION:
        return MessageService.translate('text.adult-tpn');
      case PrescriptionType.NEONATAL_PARENTERAL_NUTRITION:
      case PrescriptionType.NEONATAL_PARENTERAL_PEDIATRIC:
        return MessageService.translate('text.neonatal-children');
      default:
        return description;
    }
  }

  const getStringValue = (value: unknown): string => {
    if (value == null) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object') {
      return String((value as { description?: string }).description ?? '');
    }

    return '';
  };


  const getCpoeMaterialSubtext = (): string => {
    if (!material) {
      return '';
    }

    switch (itemType) {
      case PrescriptionType.SOLUTION:
        return getStringValue(prescriptionDescription);
      case PrescriptionType.MEDICINE:
        return `${getStringValue(material.applicationForm)} ${getStringValue(material.intervalDescription)}`;
      default:
        return '';
    }
  }
  const isChemotherapyAdministered = (): boolean => {
    return chemotherapy === true && !chemotherapyStatus && status === 'ADMINISTERED'
  }

  const formatMaterialList = (): TimeLineMaterials[] => {
    const materialsList: TimeLineMaterials[] = [];
    for (let i = 0; i <= 7; i++) {
      const indexName: string = i == 0 ? '' : String(i);
      const materialTemp = (material as any);
      if (materialTemp[`materialDescription${indexName}`]) {
        materialsList.push({
          dosageQuantity: materialTemp[`dosageQuantity${indexName}`],
          highSurveillance: materialTemp[`highSurveillance${indexName}`] == 'S',
          materialDescription: materialTemp[`materialDescription${indexName}`],
          therapeuticMargin: materialTemp[`therapeuticMargin${indexName}`] == 'S',
          unitMeasurement: materialTemp[`unitMeasurement${indexName}`],
        });
      }
    }

    return materialsList;
  }

  const getChemotherapyTokenList = (): TokenListItem[] => {
    const data = [
      {
        type: Type.NEUTRAL,
        value: 'text.chemotherapy'
      }
    ]
    switch (isMedication) {
      case ChemotherapyMedicationType.PRE_MEDICATION:
        data.push(
          {
            type: Type.NEUTRAL,
            value: 'text.pre-medication'
          }
        )
        break;
      case ChemotherapyMedicationType.POST_MEDICATION:
        data.push(
          {
            type: Type.NEUTRAL,
            value: 'text.after-medication'
          }
        )
        break;
      case ChemotherapyMedicationType.MEDICATION:
        data.push(
          {
            type: Type.NEUTRAL,
            value: 'text.chemotherapy-medication'
          }
        )
        break;
      default:
        break;
    }

    return data;
  }


  const getMaxPartsValue = (): number => {
    const totalSteps = Number(totalSolutionStep);
    const currentStep = Number(emarStepNumber);
    const safeTotal = Number.isFinite(totalSteps) ? totalSteps : 0;
    const safeCurrent = Number.isFinite(currentStep) ? currentStep : 0;
    return Math.max(safeTotal, safeCurrent, 1);
  }


  const renderIcon = (): ReactElement | undefined => {
    if (!isChemotherapyAdministered() && chemotherapy && (status !== 'CHECKED_FIRST' || chemotherapyStatus === ChemotherapyStatus.STARTED || !chemotherapyStatus)) {
      if (!chemotherapyStatus) {
        return getStatusIcon(status, String(itemType), Boolean(delayed), reasonForsettlementCode as number);
      }

      return getChemotherapyStatusIcon(item?.itemStatus == 'PENDING_REFUSAL' ? String(item.itemStatus) : String(chemotherapyStatus), Boolean(delayed));
    }

    return getStatusIcon(status, String(itemType), Boolean(delayed), reasonForsettlementCode as number);
  }


  return (
    <>
      <div className='timeline__container'>
        <div className='timeline__content'>
          {!material?.materialDescription && (
            <div className='timeline__text-container'>
              <span className='timeline__text'>{getDescriptionByItemType()}</span>
            </div>
          )}
          {material?.materialDescription && formatMaterialList().map(mat => (
            <div key={mat.materialDescription} className='timeline__text-container' style={{ marginBottom: '3px' }}>
              <span className='timeline__text'>{mat.materialDescription}</span>
              <span className='timeline__sub-text' style={{ marginLeft: '4px' }}>{mat.dosageQuantity} {mat.unitMeasurement}</span>
              {mat?.highSurveillance && (
                <span className='timeline__attention-tag'> {MessageService.translate('label.high-surveillance')} </span>
              )}
              {mat?.therapeuticMargin && (
                <span className='timeline__attention-tag'> {MessageService.translate('label.therapeutic-margin')} </span>
              )}
            </div>
          ))}
          {material?.diluentDescription && (
            <div className='timeline__text-container'>
              <span className='timeline__sub-text'>{material.diluentDescription} {material.diluentDosage} {material.diluentUM}</span>
            </div>
          )}
          {material && (
            <div className='timeline__text-container'>
              <span className='timeline__sub-text'> {getCpoeMaterialSubtext()} </span>
            </div>
          )}
        </div>

        {!pdoPrn && (
          <div className='timeline__icon-right'>
            {renderIcon()}
          </div>
        )}
      </div>
      {chemotherapy && (
        <TokenList
          data={getChemotherapyTokenList()}
        />)}

      {(status === MedicationStatus.STARTED && isValidInfusionType(itemType)) && (
        <div className='timeline-progress-bar_cmp'>
          <ProgressBar
            maxParts={getMaxPartsValue()}
            currentPart={Number(emarStepNumber)}
            leftText={`${Number(emarStepNumber)}/${getMaxPartsValue()}`}
          />
        </div>
      )}
    </>
  );
}

export function isValidInfusionType(itemType: Value): boolean {
  return itemType === PrescriptionType.SOLUTION ||
    itemType === PrescriptionType.ENTERAL_NUTRITION ||
    itemType === PrescriptionType.BLOOD_MANAGEMENT ||
    itemType === PrescriptionType.GASOTERAPY
}
